import { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { cn } from '@/lib/utils';
import type { NumberLineScene } from '@/types/gameSpec';
import { SCENE_LABEL, type SceneComponentProps } from './sceneCommon';

export const NumberLineSceneView = ({
  scene,
  onAnswer,
}: SceneComponentProps<NumberLineScene>) => {
  const tolerance = scene.tolerance ?? scene.step / 2;
  const startMid = useMemo(() => (scene.min + scene.max) / 2, [scene]);
  const [value, setValue] = useState<number>(startMid);
  const [revealed, setRevealed] = useState(false);

  const submit = () => {
    if (revealed) return;
    setRevealed(true);
    const ok = Math.abs(value - scene.target) <= tolerance;
    setTimeout(() => onAnswer(ok, { value, target: scene.target }), 800);
  };

  // Build tick marks
  const ticks: number[] = [];
  for (let v = scene.min; v <= scene.max + 1e-9; v += scene.step) {
    ticks.push(Number(v.toFixed(6)));
  }

  return (
    <Card>
      <CardContent className="p-6 space-y-6">
        <div>
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            {SCENE_LABEL.number_line}
          </div>
          <p className="text-lg font-medium mt-1">{scene.prompt}</p>
        </div>

        <div className="space-y-3 px-2">
          <Slider
            min={scene.min}
            max={scene.max}
            step={scene.step}
            value={[value]}
            onValueChange={(v) => !revealed && setValue(v[0])}
            disabled={revealed}
          />
          <div className="flex justify-between text-xs text-muted-foreground">
            {ticks.length <= 21
              ? ticks.map((t) => (
                  <span key={t} className="tabular-nums">
                    {t}
                  </span>
                ))
              : (
                <>
                  <span>{scene.min}</span>
                  <span>{scene.max}</span>
                </>
              )}
          </div>
          <div className="text-center text-2xl font-bold tabular-nums">
            {value}
          </div>
        </div>

        {!revealed ? (
          <Button onClick={submit}>Lock in</Button>
        ) : (
          <p
            className={cn(
              'font-medium',
              Math.abs(value - scene.target) <= tolerance
                ? 'text-green-700'
                : 'text-destructive',
            )}
          >
            Target was {scene.target} (±{tolerance})
          </p>
        )}
      </CardContent>
    </Card>
  );
};
